import React, { useEffect, useState } from "react";
import { FormItem, FormControl, FormMessage } from "../ui/form";
import { Input } from "../ui/input";
import { Button } from "../ui/button";

export interface Column {
  header: string;
  accessor: string;
  toValue?: (value:any) => any;
  fromValue?: (value:any) => any;
}

interface SimpleTableProps {
  columns: Column[];
  data: any[];
  editing?: boolean;
  onDataChange: (updatedData: any[]) => void;
}

const CompanyInputTable: React.FC<SimpleTableProps> = ({
  columns,
  data,
  editing,
  onDataChange,
}) => {
  const [tableData, setTableData] = useState(data);

  useEffect(() => {
    if (data) {
      setTableData(data);
    }
  }, [data]);

  const updateData = (updatedData: any[]) => {
    onDataChange(updatedData);
  };

  const handleInputChange = (
    accessor: string,
    value: string,
    rowIndex: number
  ) => {
    const updatedData = tableData.map((item, index) => {
      if (index === rowIndex) {
        return {
          ...item,
          [accessor]: value,
        };
      }
      return item;
    });

    setTableData(updatedData);
    updateData(updatedData);
  };

  const handleAddRow = () => {
    const newRow = columns.reduce(
      (acc, column) => ({ ...acc, [column.accessor]: "" }),
      {}
    );
    const updatedData = [...tableData, newRow];
    setTableData(updatedData);
    updateData(updatedData);
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = tableData.filter((_, index) => index !== rowIndex);
    setTableData(updatedData);
    updateData(updatedData);
  };

  return (
    <div className="">
      <div className="overflow-y-auto max-h-40 border border-gray-200 rounded-lg">
        <table className="min-w-full border-collapse font-medium text-sm text-gray-500">
          <thead className="bg-gray-200">
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  className="p-2 text-left bg-gray-100 sticky top-0 z-10"
                >
                  {column.header}
                </th>
              ))}
              {editing && tableData.length > 0 && (
                <th className="p-2 text-left bg-gray-100 sticky top-0 z-10"></th>
              )}
            </tr>
          </thead>
          <tbody>
            {tableData.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + (editing ? 1 : 0)}
                  className="text-center p-4"
                >
                  No results
                </td>
              </tr>
            ) : (
              tableData.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {columns.map((column, cellIndex) => (
                    <td
                      key={cellIndex}
                      className="p-2 border-b border-gray-300"
                    >
                      {editing ? (
                        <FormItem>
                          <FormControl>
                            <Input
                              className="w-full border-none"
                              placeholder={`Enter ${column.header}`}
                              value={column.toValue? column.toValue(row[column.accessor]) : row[column.accessor]}
                              onChange={(e) =>
                                handleInputChange(
                                  column.accessor,
                                  column.fromValue? column.fromValue(e.target.value) : e.target.value,
                                  rowIndex
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      ) : (
                        <div className="mt-2 text-black">
                          <span>{column.toValue? column.toValue(row[column.accessor]) : row[column.accessor]}</span>
                        </div>
                      )}
                    </td>
                  ))}
                  {editing && (
                    <td className="p-2 border-b border-gray-300">
                      <Button
                        className="hover:bg-gray-50"
                        variant="ghost"
                        type="button"
                        onClick={() => handleDeleteRow(rowIndex)}
                      >
                        Delete
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {editing && (
        <div className="mt-2">
          <Button variant="outline" type="button" onClick={handleAddRow}>
            Add
          </Button>
        </div>
      )}
    </div>
  );
};

export default CompanyInputTable;
